import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { logsApi } from "@/api/client";
import type { LogEntry, LogsQuery, LogsResponse } from "@/types/api";
import { scrollViewportOf } from "./logAutoScroll";
import {
  canLoadOlder,
  firstVisibleRow,
  type HistoryStatus,
  isAtTop,
  mergePage,
  nextHistoryQuery,
  pageOutcome,
  type RowAnchor,
  reachedTop,
  type ScrollMetrics,
} from "./logHistory";

/** A scroll element's geometry at one moment — the element itself keeps
 *  moving, so the rules compare against a copy. */
function snapshot(viewport: ScrollMetrics): ScrollMetrics {
  return {
    scrollTop: viewport.scrollTop,
    scrollHeight: viewport.scrollHeight,
    clientHeight: viewport.clientHeight,
  };
}

/** Scrolls the list so the row with `key` sits `offset` px below the
 *  viewport's top edge. The list owns the virtualizer that knows where a
 *  row starts, so it provides this; false when the key is not shown. */
export type RestoreAnchor = (anchor: RowAnchor) => boolean;

/** The rendered rows' boxes relative to the viewport's top edge, keyed
 *  by entry (the rows carry `data-key`). */
function renderedRows(container: HTMLElement, viewport: HTMLElement) {
  const top = viewport.getBoundingClientRect().top;
  return Array.from(
    container.querySelectorAll<HTMLElement>("[data-key]"),
    (row) => {
      const box = row.getBoundingClientRect();
      return {
        key: row.dataset.key ?? "",
        top: box.top - top,
        bottom: box.bottom - top,
      };
    },
  );
}

// Keeps the row under the reader where it is when a page is merged in:
// `capture`, called just before the merge, notes which row sits under
// the viewport's top edge and how far into it the reader is; the layout
// effect — pre-paint, once the page has laid out — puts that row back
// there. By key, not by how much taller the list became: a merge can
// also fill a gap below the reader, which must not move them.
function useScrollAnchor(
  containerRef: RefObject<HTMLDivElement>,
  restoreAnchorRef: RefObject<RestoreAnchor | null>,
  logs: LogEntry[],
) {
  const anchorRef = useRef<RowAnchor | null>(null);

  const capture = useCallback(() => {
    const container = containerRef.current;
    const viewport = scrollViewportOf(container);
    anchorRef.current =
      container && viewport
        ? firstVisibleRow(renderedRows(container, viewport), 0)
        : null;
  }, [containerRef]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: logs is the trigger — the anchor was taken for this very update
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    anchorRef.current = null;
    if (anchor) restoreAnchorRef.current?.(anchor);
  }, [logs, restoreAnchorRef]);

  return capture;
}

// Which live window a request belongs to. Resume and a job change start
// a new one, as does unmount; a response from an earlier window — still
// in flight when the buffer was reset — must not touch the new one.
// `onReset` runs at each new window so the caller can clear its own
// per-window state.
function useHistorySession(
  isStreaming: boolean,
  jobFilter: string,
  onReset: () => void,
) {
  const sessionRef = useRef(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: jobFilter is an intentional trigger — a job change reconnects and resets the buffer; onReset is stable
  useEffect(() => {
    if (!isStreaming) return;
    sessionRef.current += 1;
    onReset();
  }, [isStreaming, jobFilter]);

  useEffect(
    () => () => {
      sessionRef.current += 1;
    },
    [],
  );

  const current = useCallback(() => sessionRef.current, []);
  const isCurrent = useCallback(
    (session: number) => session === sessionRef.current,
    [],
  );
  return { current, isCurrent };
}

// Scroll gestures that mean "older, please": reaching the top, or
// wheeling up while already there — a list too short to scroll (a narrow
// level filter, a quiet window) emits no scroll event for that gesture.
// A scroll event only counts as an upward move onto the top of a list
// that overflows (`reachedTop`): a list that shrinks under a narrower
// filter clamps the offset to 0 and fires the same event, and must not
// start paging on its own; a move down inside the top band is the
// reader heading the other way.
function useTopTrigger(
  containerRef: RefObject<HTMLDivElement>,
  loadOlder: () => void,
) {
  const previousRef = useRef<ScrollMetrics | null>(null);

  const handleScroll = useCallback(() => {
    const viewport = scrollViewportOf(containerRef.current);
    if (!viewport) return;
    const previous = previousRef.current ?? snapshot(viewport);
    previousRef.current = snapshot(viewport);
    if (reachedTop(viewport, previous)) loadOlder();
  }, [containerRef, loadOlder]);

  const handleWheel = useCallback(
    (deltaY: number) => {
      if (deltaY >= 0) return;
      const viewport = scrollViewportOf(containerRef.current);
      if (viewport && isAtTop(viewport)) loadOlder();
    },
    [containerRef, loadOlder],
  );

  return { handleScroll, handleWheel };
}

type PageResult = { ok: true; response: LogsResponse } | { ok: false };

/** The request itself, with failure as a value: the caller decides what
 *  a failure means for the view, not where it is caught. */
async function fetchPage(query: LogsQuery): Promise<PageResult> {
  try {
    return { ok: true, response: await logsApi.get(query) };
  } catch {
    return { ok: false };
  }
}

export interface LogHistoryDeps {
  logs: LogEntry[];
  jobFilter: string;
  isStreaming: boolean;
  containerRef: RefObject<HTMLDivElement>;
  pauseStreaming: () => void;
  /** Stops the tail following for now (the saved preference stays): a
   *  wheel up on a list too short to scroll fires no scroll event, and a
   *  list that does not overflow reads as "at the bottom", so the pin
   *  would otherwise override the inserted page's anchor. */
  disengageAutoScroll: () => void;
  /** Replaces the view with a page merged into it (see `mergePage`). */
  replaceEntries: (merged: LogEntry[]) => void;
}

// Browsing back (TTTL-90): scrolling to the top of the list pulls the
// next older page from GET /logs and inserts it above, with the scroll
// position corrected so the row under the reader stays put. The first
// pull pauses the tail. Resume, or a job change, puts history back to
// its start.
export function useLogHistory({
  logs,
  jobFilter,
  isStreaming,
  containerRef,
  pauseStreaming,
  disengageAutoScroll,
  replaceEntries,
}: LogHistoryDeps) {
  const [status, setStatus] = useState<HistoryStatus>("idle");
  // Scroll events arrive faster than a render: the ref stops a second
  // request slipping in before `status` catches up.
  const inFlightRef = useRef(false);
  // Entries the last page showed above the view: the store kept filling
  // after the tail paused, and the next window widens by this much so
  // the page still reaches back (see nextHistoryQuery).
  const newerSeenRef = useRef(0);
  // Filled by the list with its way of putting an anchored row back; only
  // the virtualizer knows where a row starts (see `RestoreAnchor`).
  const restoreAnchorRef = useRef<RestoreAnchor | null>(null);
  const captureAnchor = useScrollAnchor(containerRef, restoreAnchorRef, logs);

  // A live window is a fresh start: whatever was browsed before Resume
  // (or under the previous job) is gone with the buffer reset, and so
  // is any request that was still out for it.
  const resetHistory = useCallback(() => {
    inFlightRef.current = false;
    newerSeenRef.current = 0;
    setStatus("idle");
  }, []);
  const session = useHistorySession(isStreaming, jobFilter, resetHistory);

  const loadOlder = useCallback(async () => {
    if (!canLoadOlder(status) || inFlightRef.current) return;
    const started = session.current();
    inFlightRef.current = true;
    setStatus("loading");
    pauseStreaming();
    disengageAutoScroll();
    const query = nextHistoryQuery(
      logs.length,
      jobFilter,
      newerSeenRef.current,
    );
    const result = await fetchPage(query);
    // A window that ended while the request was out owns none of this.
    if (!session.isCurrent(started)) return;
    inFlightRef.current = false;
    if (!result.ok) {
      // The tail is already paused, so say so; the next scroll up asks
      // again.
      setStatus("failed");
      return;
    }
    const { merged, added, newer } = mergePage(logs, result.response.logs);
    newerSeenRef.current = newer;
    captureAnchor();
    if (added > 0) replaceEntries(merged);
    setStatus(pageOutcome(added, result.response, query.limit ?? 0, newer));
  }, [
    status,
    logs,
    jobFilter,
    session,
    captureAnchor,
    pauseStreaming,
    disengageAutoScroll,
    replaceEntries,
  ]);

  const { handleScroll, handleWheel } = useTopTrigger(containerRef, () => {
    void loadOlder();
  });

  return { status, handleScroll, handleWheel, restoreAnchorRef };
}
